/**
 * Black Panther — „Marvel: For King and Country".
 *
 * VIBRANIUM SUIT: niektoré karty uložia súperovu kartu (z vrchu jeho balíčka)
 * do obleku — leží lícom hore (`players[i].suit`), do ruky ani balíčka sa
 * nepočíta a dá sa použiť **len na boost** (v manévri aj v súboji). Po boostovaní
 * ide do odhodu svojho majiteľa a jej „BOOSTED WITH" efekt sa vyrieši normálne.
 * Každý boost Black Panthera mu dá 1 kartu.
 * (Unmatched Rules Reference — Black Panther: Vibranium Suit)
 */

import { shareZone } from '../map.js';
import type { Ctx } from '../ops.js';
import { cardDef, drawCard, gainAction, getMap, healFighter, storeFromDeck } from '../ops.js';
import type { PlayerIndex } from '../types.js';
import { draw, feint, regroup, skirmish } from './common.js';
import { askBoost, askMove, fighterOrNull, opponentOf } from './helpers.js';
import type { CardEffect, EffectRegistry, HeroSpecial } from './types.js';

const PANTHER = 'black-panther';
const SHURI = 'shuri';

const figure = (ctx: Ctx, player: PlayerIndex, defId: string) =>
  ctx.state.fighters.find((f) => f.owner === player && f.defId === defId) ?? null;

/** „Reveal the top card of your opponent's deck and store it in your Vibranium Suit." */
const analyzeAndAdjust: CardEffect = {
  after: (e) => {
    const opponent = opponentOf(e);
    if (opponent !== null) storeFromDeck(e.ctx, e.player, opponent, 1);
  },
};

/** „If you won the combat, reveal the top card of your opponent's deck and store it…" */
const ancestralInsight: CardEffect = {
  after: (e) => {
    const opponent = opponentOf(e);
    if (e.won && opponent !== null) storeFromDeck(e.ctx, e.player, opponent, 1);
  },
};

/**
 * „Add the BOOST value of the opposing fighter's card to this card's value.
 * Then, you may BOOST this card." Hodnota súperovej karty je verejná (odhalená).
 */
const antiMetalClaws: CardEffect = {
  duringChoice: (e) => askBoost(e, 'Anti-Metal Claws: smieš boostovať túto kartu'),
  during: (e, value) => {
    const combat = e.ctx.state.combat;
    const theirs = e.role === 'defender' ? combat?.attackCard : combat?.defenseCard;
    if (!theirs || (e.role === 'defender' && combat?.bonus)) return value;
    return value + cardDef(e.ctx, theirs).boost;
  },
};

/**
 * „Shuri recovers 1 health. If Black Panther is in the same zone as Shuri, he
 * also recovers 1 health."
 */
const nanotriageProcessor: CardEffect = {
  after: (e) => {
    const shuri = figure(e.ctx, e.player, SHURI);
    const panther = figure(e.ctx, e.player, PANTHER);
    if (shuri) healFighter(e.ctx, shuri.id, 1);
    if (shuri && panther && shareZone(getMap(e.ctx), shuri.spaceId, panther.spaceId)) healFighter(e.ctx, panther.id, 1);
  },
};

/** „Move each of your fighters up to 3 spaces. They may move through opposing fighters. Gain 1 action." */
const stalkingPanther: CardEffect = {
  scheme: (e) => {
    const ids = e.ctx.state.fighters.filter((f) => f.owner === e.player).map((f) => f.id);
    for (const id of ids) {
      askMove(e, {
        fighters: [id],
        max: 3,
        through: true,
        label: `Stalking Panther: presuň ${fighterOrNull(e.ctx, id)?.name ?? 'figúrku'} až o 3 (smie prejsť cez súperov)`,
      });
    }
    gainAction(e.ctx, e.player);
  },
};

/** „Choose an opponent. Reveal the top 2 cards of their deck and store them in your Vibranium Suit." */
const tacticalRemoteScanning: CardEffect = {
  scheme: (e) => {
    const opponent = opponentOf(e);
    if (opponent !== null) storeFromDeck(e.ctx, e.player, opponent, 2);
  },
};

/** „You may BOOST this card up to two times." */
const wakandaForever: CardEffect = {
  duringChoice: (e) => {
    askBoost(e, 'Wakanda Forever: prvý boost (smieš dvakrát)');
    askBoost(e, 'Wakanda Forever: druhý boost');
  },
};

/** „DURING COMBAT: You may BOOST this card." */
const mayBoost = (name: string): CardEffect => ({
  duringChoice: (e) => askBoost(e, `${name}: smieš boostovať túto kartu`),
});

export const blackPantherCards: EffectRegistry = {
  'black-panther.analyze-and-adjust': analyzeAndAdjust,
  'black-panther.ancestral-insight': ancestralInsight,
  'black-panther.anti-metal-claws': antiMetalClaws,
  'black-panther.cat-like-reflexes': skirmish(3),
  'black-panther.evade': draw(1),
  'black-panther.feint': feint,
  'black-panther.nanotriage-processor': nanotriageProcessor,
  'black-panther.regroup': regroup,
  'black-panther.stalking-panther': stalkingPanther,
  'black-panther.tactical-remote-scanning': tacticalRemoteScanning,
  'black-panther.wakanda-forever': wakandaForever,
  'black-panther.vibranium-shockwave': mayBoost('Vibranium Shockwave'),
  'black-panther.microweave-mesh': mayBoost('Microweave Mesh'),
};

export const blackPantherSpecial: HeroSpecial = {
  name: 'Vibranium Suit',
  /** „Whenever you BOOST, draw 1 card." */
  onBoost: (ctx, player) => {
    drawCard(ctx, player);
  },
};
